import React, { useState, useEffect, useContext, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { ThemeContext } from '../../context/ThemeContext';
import { useMetadata } from '../../context/MetadataContext';
import { useGameState } from '../../context/GameStateContext';
import { useGlobalError } from '../../context/GlobalErrorContext';
import styles from './NewPuzzle.module.css';

/**
 * NewPuzzle modal component for creating a new puzzle
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 */
const NewPuzzle = ({ isOpen, onClose }) => {
  const { isDarkMode } = useContext(ThemeContext);
  const { getText, metadata } = useMetadata();
  const { createPuzzle } = useGameState();
  const { triggerError } = useGlobalError();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState('calcudoku');
  const [selectedSize, setSelectedSize] = useState(4);
  const [selectedStartType, setSelectedStartType] = useState('solved'); // Default to solved grid

  // Update selectedSize when selectedStyle changes if current size is not available
  useEffect(() => {
    if (!metadata?.create?.available_options?.[selectedStyle]) {
      return;
    }

    const availableSizes = metadata.create.available_options[selectedStyle];

    // If current size is not available for this style, select the first available size
    if (!availableSizes.includes(selectedSize)) {
      if (availableSizes.length > 0) {
        setSelectedSize(availableSizes[0]);
      }
    }
  }, [selectedStyle, metadata, selectedSize]);

  // Add event listener to close modal on escape key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent scrolling on the body when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      // Restore scrolling when modal is closed
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  // Close modal when clicking outside of content
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleCreate = async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await createPuzzle(selectedStartType, selectedStyle, selectedSize);
      onClose(); // Close modal on success
    } catch (err) {
      console.error('Error creating puzzle:', err);
      setError(err.response?.data?.message || 'Failed to create puzzle. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Generate puzzle styles dynamically from metadata
  const puzzleStyles = useMemo(() => {
    if (!metadata?.create?.available_options) {
      return [];
    }

    return Object.keys(metadata.create.available_options).map(styleKey => ({
      value: styleKey,
      label: styleKey.charAt(0).toUpperCase() + styleKey.slice(1).replace('_', ' ')
    }));
  }, [metadata]);
  
  // Generate puzzle sizes dynamically based on selected style
  const puzzleSizes = useMemo(() => {
    if (!metadata?.create?.available_options?.[selectedStyle]) {
      return [];
    }

    return metadata.create.available_options[selectedStyle].map(size => ({
      value: size,
      label: `${size}x${size}`
    }));
  }, [metadata, selectedStyle]);
  
  const startTypes = [
    {
      value: 'blank',
      label: getText('create.blank_grid'),
      description: getText('create.blank_description')
    },
    {
      value: 'solved',
      label: getText('create.solved_grid'),
      description: getText('create.solved_description')
    },
    {
      value: 'auto',
      label: getText('create.auto_puzzle'),
      description: getText('create.auto_description')
    }
  ];

  // Get the description for the currently selected start type
  const selectedStartTypeData = startTypes.find(type => type.value === selectedStartType);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={styles.modalBackdrop} onClick={handleBackdropClick}>
      <div className={`${styles.modalContent} ${isDarkMode ? styles.darkMode : styles.lightMode}`}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {getText('create.new_puzzle')}
          </h2>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}
          
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              {getText('common_text.style')}:
            </label>
            <select 
              className={styles.select}
              value={selectedStyle}
              onChange={(e) => setSelectedStyle(e.target.value)}
              disabled={loading}
            >
              {puzzleStyles.map(style => (
                <option key={style.value} value={style.value}>
                  {style.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              {getText('common_text.size')}:
            </label>
            <select 
              className={styles.select}
              value={selectedSize}
              onChange={(e) => setSelectedSize(Number(e.target.value))}
              disabled={loading}
            >
              {puzzleSizes.map(size => (
                <option key={size.value} value={size.value}>
                  {size.label}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              {getText('create.start_label')}:
            </label>
            <select 
              className={styles.select}
              value={selectedStartType}
              onChange={(e) => setSelectedStartType(e.target.value)}
              disabled={loading}
            >
              {startTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {selectedStartTypeData && (
              <div className={styles.startTypeDescription}>
                {selectedStartTypeData.description}
              </div>
            )}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button 
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={loading}
          >
            {getText('common_text.close')}
          </button>
          <button 
            className={styles.createButton}
            onClick={handleCreate}
            disabled={loading}
          >
            {loading ? (getText('common_text.loading')) : (getText('actions.action_create'))}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NewPuzzle;